import { FIXED_DT, MAX_FRAME_TIME } from '../config/config';

export interface LoopCallbacks {
  /** Called once per rendered frame before the fixed steps. */
  beginFrame?(): void;
  /** Fixed-timestep logic. `step` is the index of this step within the frame. */
  update(dt: number, step: number): void;
  /** Called once per rendered frame. `alpha` is the leftover accumulator fraction. */
  render(alpha: number): void;
}

/**
 * requestAnimationFrame driver with a fixed-timestep accumulator.
 *
 * - Physics always advances in FIXED_DT slices, so 60Hz / 120Hz / 144Hz screens all
 *   produce identical movement.
 * - Long stalls (tab switch, GC, window drag) are clamped instead of being simulated
 *   in one huge catch-up burst, which is what normally makes characters teleport.
 */
export class Loop {
  private raf = 0;
  private last = 0;
  private acc = 0;
  private running = false;

  /** Smoothed frames per second, for the debug overlay. */
  fps = 0;
  /** Logic steps executed on the previous frame. */
  steps = 0;

  constructor(private cb: LoopCallbacks) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = now();
    this.acc = 0;
    this.raf = requestAnimationFrame(this.frame);
    window.addEventListener('blur', this.resetClock);
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    cancelAnimationFrame(this.raf);
    window.removeEventListener('blur', this.resetClock);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /** Forget accumulated time (after a stall the world must not fast-forward). */
  resetClock = (): void => {
    this.last = now();
    this.acc = 0;
  };

  private onVisibility = (): void => {
    if (!document.hidden) this.resetClock();
  };

  private frame = (t: number): void => {
    if (!this.running) return;
    this.raf = requestAnimationFrame(this.frame);

    let frameTime = (t - this.last) / 1000;
    this.last = t;
    if (!isFinite(frameTime) || frameTime < 0) frameTime = 0;
    if (frameTime > MAX_FRAME_TIME) frameTime = MAX_FRAME_TIME;

    this.fps = this.fps === 0 ? 1 / Math.max(frameTime, 1e-4) : this.fps * 0.9 + (1 / Math.max(frameTime, 1e-4)) * 0.1;

    this.cb.beginFrame?.();

    this.acc += frameTime;
    let steps = 0;
    while (this.acc >= FIXED_DT) {
      this.cb.update(FIXED_DT, steps);
      this.acc -= FIXED_DT;
      steps++;
      // Hard safety valve: never spin more than this in one frame.
      if (steps > 40) {
        this.acc = 0;
        break;
      }
    }
    this.steps = steps;
    this.cb.render(this.acc / FIXED_DT);
  };
}

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}
